"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_FETCH_ALL_LIMIT = void 0;
const client_library_1 = require("@seneca/client-library");
const errorMapping_1 = __importDefault(require("./errorMapping"));
const ForbiddenError_1 = __importDefault(require("./errors/ForbiddenError"));
const NoValidSubscriptionExistsError_1 = __importDefault(require("./errors/NoValidSubscriptionExistsError"));
const StripeError_1 = __importDefault(require("./errors/StripeError"));
const UnknownError_1 = __importDefault(require("./errors/UnknownError"));
const ValidationFailureOnStripeError_1 = __importDefault(require("./errors/ValidationFailureOnStripeError"));
const InvalidSubscriptionProductIdError_1 = __importDefault(require("./errors/InvalidSubscriptionProductIdError"));
exports.DEFAULT_FETCH_ALL_LIMIT = 2000;
class SubscriptionsServiceClient extends client_library_1.Client {
    constructor(fetch, options = {}) {
        super(fetch, options);
    }
    async createSubscription(options) {
        this._validateMissingParams({
            paymentMethodId: options.paymentMethodId
        });
        this._validateMissingParams({
            subscriptionProductId: options.subscriptionProductId
        });
        return await this._makeRequest('POST', 'api/subscriptions', options);
    }
    async fetchPaymentIntentClientSecret() {
        return await this._makeRequest('GET', 'api/subscriptions/me/client-secret');
    }
    async fetchPaymentIntentClientSecretById(subscriptionId) {
        this._validateMissingParams({
            subscriptionId
        });
        return await this._makeRequest('GET', `api/subscriptions/client-secret/${subscriptionId}`);
    }
    async createPaymentIntent(body) {
        var _a;
        const url = `${this.url}/api/subscriptions/payment-intent`;
        const options = { method: 'POST', body: JSON.stringify(body) };
        const expectedResponses = [200, 400];
        const response = await this.requestMaker.makeRequest(url, options, expectedResponses);
        if (response.status === 400) {
            const reason = (_a = response.json) === null || _a === void 0 ? void 0 : _a.reason;
            if (reason === 'InvalidSubscriptionProductId') {
                throw new InvalidSubscriptionProductIdError_1.default();
            }
            throw new StripeError_1.default(reason !== null && reason !== void 0 ? reason : '');
        }
        return response.json;
    }
    async retrySubscriptionPayment(options) {
        this._validateMissingParams({
            paymentMethodId: options === null || options === void 0 ? void 0 : options.paymentMethodId
        });
        return await this._makeRequest('POST', 'api/subscriptions/retry', options);
    }
    async retrySubscriptionPaymentById(subscriptionId, options) {
        this._validateMissingParams({
            subscriptionId: subscriptionId,
            paymentMethodId: options === null || options === void 0 ? void 0 : options.paymentMethodId
        });
        return await this._makeRequest('POST', `api/subscriptions/retry/${subscriptionId}`, options);
    }
    async cancelMySubscription() {
        return await this._makeRequest('DELETE', 'api/subscriptions/me');
    }
    async cancelSubscriptionById(subscriptionId) {
        this._validateMissingParams({
            subscriptionId: subscriptionId
        });
        return await this._makeRequest('DELETE', `api/subscriptions/${subscriptionId}`);
    }
    async restartMySubscription() {
        return await this._makeRequest('POST', 'api/subscriptions/me/restart');
    }
    async restartSubscriptionById(subscriptionId) {
        this._validateMissingParams({
            subscriptionId
        });
        return await this._makeRequest('POST', `api/subscriptions/restart/${subscriptionId}`);
    }
    async fetchMySubscription() {
        return await this._makeRequest('GET', 'api/subscriptions/me');
    }
    async confirmSubscriptionStatus() {
        return await this._makeRequest('POST', 'api/subscriptions/me/confirm');
    }
    async upgradeMySubscription(options) {
        this._validateMissingParams({
            subscriptionProductId: options === null || options === void 0 ? void 0 : options.subscriptionProductId
        });
        return await this._makeRequest('POST', 'api/subscriptions/me/upgrade', options);
    }
    async upgradeSubscriptionById(subscriptionId, options) {
        this._validateMissingParams({
            subscriptionId,
            subscriptionProductId: options === null || options === void 0 ? void 0 : options.subscriptionProductId
        });
        return await this._makeRequest('POST', `api/subscriptions/upgrade/${subscriptionId}`, options);
    }
    async previewMySubscriptionUpgrades() {
        return await this._makeRequest('GET', 'api/subscriptions/me/preview-upgrades');
    }
    async previewSubscriptionUpgradesById(subscriptionId) {
        this._validateMissingParams({
            subscriptionId
        });
        return await this._makeRequest('GET', `api/subscriptions/preview-upgrades/${subscriptionId}`);
    }
    async updateSubscriptionStatusById(subscriptionId) {
        this._validateMissingParams({
            subscriptionId
        });
        return await this._makeRequest('POST', `api/subscriptions/updateSubscriptionStatus/${subscriptionId}`);
    }
    async reassignSubscriptionById(options) {
        const { subscriptionId, reassignToUserEmail, reassignToUserId, requesterUserId } = options;
        this._validateMissingParams({
            subscriptionId,
            reassignToUserId,
            reassignToUserEmail,
            requesterUserId
        });
        const allowedResponses = [204, 400, 403, 404, 422];
        const url = `${this.url}/api/admin/subscriptions/reassign/${subscriptionId}`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({
                reassignToUserEmail,
                reassignToUserId,
                requesterUserId
            })
        }, allowedResponses);
        if (response.status === 400) {
            throw new ValidationFailureOnStripeError_1.default();
        }
        if (response.status === 403) {
            throw new ForbiddenError_1.default();
        }
        if (response.status === 404) {
            throw new NoValidSubscriptionExistsError_1.default();
        }
        return response.json;
    }
    async fetchSubscriptionsWithActivitySummary(limit) {
        const standardProducts = await this.fetchSubscriptionProducts();
        const misProducts = await this.fetchSubscriptionProducts({
            includeManagedProducts: true
        });
        const products = [...standardProducts, ...misProducts];
        const productIdsWithSummary = products
            .filter(prod => prod.product.parentActivitySummary)
            .map(prod => prod.id);
        const allSubscriptions = await this.fetchAllActiveSubscriptions(limit);
        return allSubscriptions.reduce((subscriptionsWithActivitySummary, sub) => {
            var _a;
            if (productIdsWithSummary.includes(sub.subscriptionProductId) ||
                ((_a = sub.addonProductIds) === null || _a === void 0 ? void 0 : _a.some(prodId => productIdsWithSummary.includes(prodId)))) {
                const product = products.find(prod => prod.id === sub.subscriptionProductId);
                return [...subscriptionsWithActivitySummary, { ...sub, product }];
            }
            return subscriptionsWithActivitySummary;
        }, []);
    }
    async fetchAllActiveSubscriptions(limit) {
        return this.fetchAllSubscriptions({
            isActive: true,
            limit: limit || exports.DEFAULT_FETCH_ALL_LIMIT
        });
    }
    async fetchAllSubscriptions(options) {
        return this._pageMethod({
            method: this.fetchPageOfSubscriptions.bind(this),
            options
        });
    }
    async fetchPageOfSubscriptions(options) {
        return this._makeRequest('GET', 'api/admin/subscriptions', undefined, options);
    }
    async fetchBatchSubscriptions(userIds) {
        this._validateMissingParams({ userIds });
        return this._makeRequest('POST', 'api/subscriptions/batch/get', { userIds });
    }
    async fetchSubscriptionProducts(options = {}) {
        return this._pageMethod({
            method: this._fetchPageOfSubscriptionProducts.bind(this),
            options
        });
    }
    async _fetchPageOfSubscriptionProducts(options) {
        return this._makeRequest('GET', 'api/subscriptions/products', undefined, options);
    }
    async fetchPromotedSubscriptionProducts() {
        return this.fetchSubscriptionProducts({ promoted: true, addon: true });
    }
    async updatePaymentMethod(options) {
        this._validateMissingParams({
            paymentMethodId: options.paymentMethodId
        });
        return await this._makeRequest('POST', 'api/subscriptions/payment-method', options);
    }
    async updatePaymentMethodById(subscriptionId, options) {
        this._validateMissingParams({
            subscriptionId,
            paymentMethodId: options.paymentMethodId
        });
        return await this._makeRequest('POST', `api/subscriptions/payment-method/${subscriptionId}`, options);
    }
    async _makeRequest(method, path, body, queryParams, allowedResponses) {
        let url = this._constructUrl(path);
        if (queryParams && Object.keys(queryParams).length > 0) {
            let queryParamParts = Object.keys(queryParams)
                .filter(param => !!queryParams[param])
                .map(param => `${param}=${encodeURIComponent(queryParams[param])}`);
            url += `?${queryParamParts.join('&')}`;
        }
        const expectedResponses = allowedResponses !== null && allowedResponses !== void 0 ? allowedResponses : [
            200,
            201,
            204,
            400,
            404,
            422
        ];
        const response = await this.requestMaker.makeRequest(url, {
            method,
            body: body ? JSON.stringify(body) : undefined
        }, expectedResponses);
        if (response.status === 400 || response.status === 404) {
            if (!response.json) {
                throw new NoValidSubscriptionExistsError_1.default();
            }
            // Support both responses
            // - { status: 400, json: 'ErrorCode' }
            // - { status: 400, json: { reason: 'ErrorCode' }}
            if (typeof response.json === 'string') {
                if (errorMapping_1.default[response.json]) {
                    throw new errorMapping_1.default[response.json]();
                }
                throw new StripeError_1.default(response.json);
            }
            if (typeof response.json.reason === 'string') {
                if (errorMapping_1.default[response.json.reason]) {
                    throw new errorMapping_1.default[response.json.reason]();
                }
                throw new StripeError_1.default(response.json.reason);
            }
            throw new UnknownError_1.default();
        }
        return response.json;
    }
    _constructUrl(path) {
        let url = this.url;
        if (!url.endsWith('/')) {
            url += '/';
        }
        url += path;
        return url;
    }
}
exports.default = SubscriptionsServiceClient;
//# sourceMappingURL=SubscriptionsServiceClient.js.map